import { Button, Center, Flex, Image, Paper, Text, Title } from "@mantine/core";
import { FormattedMessage } from "react-intl";
import { FooterButtons } from "../../footerButtons";
import { SigningComplete } from "../../../components/signingComplete";
import {
  useCreateBooking,
  useCreatePOA,
  useCreateUser,
  useGetFetchPOAText,
  useGetSignStatus,
  useSignPOA,
  useCancelTransaction,
} from "../../../hooks/api";

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { captureException } from "@sentry/react";
import {
  isFullCredentials,
  useUpdateCustomerData,
  type BookingDetails,
} from "../../../hooks/ui/customerDataStore";
import {
  ENUMPOASteps,
  usePOAStepsStore,
} from "../../../hooks/ui/poaStepsDataStore";
import { useMediaQuery } from "@mantine/hooks";
import FullmaktskollenImage from "/fullmaktskollen-logo.png";

import { ContactDetails } from "../../contactUsPaper/contactDetails";
import { BankIdQrCode } from "../../bankID/qrCode";
import POASign from "../poaSign";
import { useBankIdAutoOpen } from "../../../hooks/ui/useBankIdAutoOpen";
import { buildBankIdUrl, openBankId } from "../../../utils/bankIdRedirect";
import { RetryErrorCard } from "../../bankID/retryErrorCard";
import { useSearch } from "@tanstack/react-router";
import { BankIdPendingMessage } from "../../login/pendingMessage";
import { LoaderComponent } from "../../loaderComponent";
import { LoaderView } from "../../appWebview/LoaderView";
import { v4 as uuidV4 } from "uuid";
import { isAxiosError } from "axios";
import { formatPoaDate } from "../../../hooks/api/useGetPoaStatus";

// The /fmk/store response, or the marker the 409 paths resolve with instead.
type CreatedPoa = {
  POAId?: string;
  validTo?: string;
  alreadyExists?: boolean;
  blocked?: boolean;
};

// A 409 arrives on the axios response, or on the error itself once the api client
// has unwrapped it.
const errorStatus = (error: unknown): number | undefined =>
  isAxiosError(error)
    ? error.response?.status
    : (error as { status?: number } | null)?.status;

// 409 is shared by "a POA already exists" (plain string body) and "the customer is
// blocked from signing" ({ code: 'POA_BLOCKED', blockedUntil }), so the code decides.
const blockedUntilOf = (error: unknown): string | undefined => {
  const body: { code?: string; blockedUntil?: string } | undefined =
    isAxiosError(error)
      ? error.response?.data
      : (error as { data?: { code?: string; blockedUntil?: string } } | null)
          ?.data;
  return body?.code === "POA_BLOCKED" ? body.blockedUntil : undefined;
};

export default function POABankID() {
  const params = useSearch({ strict: false });
  const agentId = params.s || null;
  const { customerData, updateBooking, updatePoa } = useUpdateCustomerData();
  const { setStep, prevStep, renewal, app } = usePOAStepsStore();
  const [isSignUpInProcess, setIsSignUpInProcess] = useState(false);
  const [complete, setComplete] = useState(false);
  const [processError, setProcessError] = useState(false);
  const [returnedFromBankId, setReturnedFromBankId] = useState(false);
  const [resumedTransactionId, setResumedTransactionId] = useState<
    string | null
  >(null);
  const hasInitializedSign = useRef(false);
  const hasHandledComplete = useRef(false);
  const nonceRef = useRef<string | null>(null);
  const createSign = useSignPOA();
  const cancelSign = useCancelTransaction();
  const createPOA = useCreatePOA();
  const createUser = useCreateUser();
  const createBooking = useCreateBooking();

  const queryClient = useQueryClient();
  const poaBlockedUntil = blockedUntilOf(createPOA.error);
  const poaAlreadyExists =
    errorStatus(createPOA.error) === 409 && !poaBlockedUntil;

  const poaOngoingFormatted =
    customerData.validTo === null
      ? "Tillsvidare"
      : new Date(customerData.validFrom).toISOString().split("T")[0];
  const isMobile = useMediaQuery("(max-width: 56.25em)");

  const htmlSignString = `
      <p>Jag skriver under informationsfullmakten</p>
      <p><b>Fullmaktshavare: 556974-8378, Sambla Group AB</b></p>
      <p><b>Fullmaktsgivare: ${customerData.personalNumber}, ${customerData.name} </b></p>
      <p><b>Giltighetstid: ${poaOngoingFormatted} ${customerData.validTo ? " - " + new Date(customerData.validTo).toISOString().split("T")[0] : ""}</b></p>
      `;

  const resolvedTransactionId =
    resumedTransactionId ?? createSign.data?.body?.id;

  const getSignStatus = useGetSignStatus({
    id: resolvedTransactionId,
    enabled:
      (!!resumedTransactionId || createSign.isSuccess) &&
      !hasHandledComplete.current &&
      isFullCredentials(customerData.credentials),
    credentials: customerData.credentials,
  });

  const signProcessStatusInfo =
    getSignStatus.data?.body?.providerInfo?.seBankID?.processStatusInfo;

  const autoStartToken =
    getSignStatus.data?.body?.providerInfo?.seBankID?.autoStartToken;
  // BankID cancels an order nobody acts on; that order needs replacing, not reopening.
  const signExpired = getSignStatus.data?.body?.status === "failed";

  // The redirect is a one-off: a refreshed order must not reopen BankID.
  const [signRefreshed, setSignRefreshed] = useState(false);
  useBankIdAutoOpen({
    autoStartToken,
    enabled: app && !signExpired && !signRefreshed,
  });

  const refreshedFor = useRef(null);

  const openSign = () => {
    // Built on the tap, from the order that is live right now.
    const url = buildBankIdUrl({
      autoStartToken,
      nonce: nonceRef.current,
      inApp: true,
    });
    if (url) openBankId(url);
  };

  const { data } = useGetFetchPOAText({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });

  const handleCreatePOA = () => {
    return new Promise<CreatedPoa>((resolve, reject) => {
      createPOA.mutate(
        {
          credentials: customerData.credentials,
          signedData: getSignStatus.data.body,
          email: customerData.email,
          validFrom: customerData.validFrom,
          validTo: customerData.validTo,
        },
        {
          onSuccess: (data) => {
            resolve(data);
          },
          onError: (error) => {
            if (blockedUntilOf(error)) {
              resolve({ blocked: true });
            } else if (errorStatus(error) === 409) {
              resolve({ alreadyExists: true });
            } else {
              reject(new Error(error.message));
            }
          },
        },
      );
    });
  };

  const handleCreateUser = () => {
    return new Promise((resolve, reject) => {
      createUser.mutate(
        {
          signedData: getSignStatus.data.body,
          email: customerData.email,
          phone: customerData.phoneNumber,
          agentId,
          identityVerificationId: customerData.identityVerificationId,
          transactionId: customerData.transactionId,
          credentials: customerData.credentials,
          origin: app ? "app" : "web",
        },
        {
          onSuccess: (data) => {
            resolve(data);
          },
          onError: (error) => {
            reject(new Error(error.message));
          },
        },
      );
    });
  };

  const handleCreateBooking = () => {
    return new Promise<BookingDetails>((resolve, reject) => {
      createBooking.mutate(
        { agentId, credentials: customerData.credentials },
        {
          onSuccess: (data) => {
            resolve(data);
          },
          onError: (error) => {
            reject(new Error(error.message));
          },
        },
      );
    });
  };

  const beginSign = () => {
    const nonce = uuidV4();
    nonceRef.current = nonce;
    localStorage.setItem("bankid_poa_sign_nonce", nonce);
    sessionStorage.setItem("bankid_poa_sign_tab", "1");
    createSign.mutate({
      ...customerData,
      signText: htmlSignString + data.signText,
      nonce,
      returnPath: window.location.pathname + window.location.search,
    });
  };

  const restartSign = async () => {
    const oldTransactionId = createSign.data?.body?.id;
    if (oldTransactionId) {
      // Errors already reported to Sentry via useCancelTransaction's onError
      await cancelSign.mutateAsync(oldTransactionId).catch(() => {});
    }
    hasHandledComplete.current = false;
    hasInitializedSign.current = false;
    // Without this the next poll still resolves to the order we just cancelled.
    setResumedTransactionId(null);
    setComplete(false);
    beginSign();
  };

  const goBack = () => {
    // An open order would collide with the one signing again starts.
    if (resolvedTransactionId) cancelSign.mutate(resolvedTransactionId);
    prevStep();
  };

  const advanceToNextStep = () => {
    if (renewal || app) {
      setStep(ENUMPOASteps.CONFIRMATION);
      return;
    }
    if (import.meta.env.VITE_BOOK_MEETING_ENABLED) {
      setStep(ENUMPOASteps.BOOKMEETING);
    } else {
      setStep(ENUMPOASteps.POASUMMARY);
    }
  };

  const executeSignupFlow = async () => {
    if (renewal) {
      const createdPOA = await handleCreatePOA();
      if (!createdPOA) {
        throw new Error("POA creation failed.");
      }
      // The signature went through but the store refused it: stay here and say so.
      if (createdPOA.blocked) {
        setIsSignUpInProcess(false);
        return;
      }
      if (!createdPOA.alreadyExists) {
        updatePoa({
          poaId: createdPOA.POAId ?? null,
          validTo: createdPOA.validTo ?? null,
          isValid: true,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["get-customer-info-by-id"],
      });
      queryClient.invalidateQueries({ queryKey: ["get-poa-status"] });
    } else {
      const createdUser = await handleCreateUser();
      if (!createdUser) {
        throw new Error("User creation failed.");
      }

      const [createdBooking, createdPOA] = await Promise.all([
        handleCreateBooking(),
        handleCreatePOA(),
      ]);

      if (!createdBooking) {
        throw new Error("Booking creation failed.");
      }
      updateBooking(createdBooking);

      if (!createdPOA) {
        throw new Error("POA creation failed.");
      }
      if (createdPOA.blocked) {
        setIsSignUpInProcess(false);
        return;
      }
      if (!createdPOA.alreadyExists) {
        updatePoa({
          poaId: createdPOA.POAId ?? null,
          validTo: createdPOA.validTo ?? null,
          isValid: true,
        });
      }
    }

    advanceToNextStep();
  };

  useEffect(() => {
    if (
      getSignStatus.data?.body?.status === "complete" &&
      !hasHandledComplete.current
    ) {
      hasHandledComplete.current = true;
      setIsSignUpInProcess(true);
      setProcessError(false);

      const processSignup = async () => {
        try {
          await executeSignupFlow();
        } catch (error) {
          captureException(error, {
            tags: {
              operation: "processSignup",
              errorType:
                error instanceof Error
                  ? error.name || "UnknownError"
                  : "UnknownError",
            },
            level: "error",
          });
          setIsSignUpInProcess(false);
          setProcessError(true);
        }
      };

      processSignup();
    }

    if (getSignStatus.data?.body?.status === "failed") {
      hasHandledComplete.current = true;

      // The summary takes minutes to read, by which time BankID has cancelled the
      // order. Replace it now so the button keeps a live link. Order matters:
      // restartSign clears the flag set above.
      if (app && refreshedFor.current !== resolvedTransactionId) {
        refreshedFor.current = resolvedTransactionId;
        setSignRefreshed(true);
        restartSign();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSignStatus.data]);

  useEffect(() => {
    if (createSign.isSuccess && createSign.data?.body?.id) {
      localStorage.setItem(
        "bankid_poa_sign_transaction_id",
        createSign.data.body.id,
      );
    }
  }, [createSign.isSuccess, createSign.data]);

  useEffect(() => {
    if (hasInitializedSign.current) return;
    hasInitializedSign.current = true;

    const hash = window.location.hash;
    const nonceMatch = hash.match(/[#&]nonce=([^&]*)/);

    if (nonceMatch) {
      const urlNonce = nonceMatch[1];
      const storedNonce = localStorage.getItem("bankid_poa_sign_nonce");

      if (storedNonce && urlNonce === storedNonce) {
        const storedTransactionId = localStorage.getItem(
          "bankid_poa_sign_transaction_id",
        );
        localStorage.removeItem("bankid_poa_sign_nonce");
        localStorage.removeItem("bankid_poa_sign_transaction_id");

        if (
          isFullCredentials(customerData.credentials) &&
          storedTransactionId
        ) {
          // Same-tab return with credentials intact, resume polling
          nonceRef.current = storedNonce;
          setResumedTransactionId(storedTransactionId);
        } else {
          // New tab opened by BankID (iOS), credentials not available here.
          // The original tab is still polling and will complete the flow.
          setReturnedFromBankId(true);
        }

        return () => {
          hasInitializedSign.current = false;
        };
      }
    }

    beginSign();

    return () => {
      hasInitializedSign.current = false;
    };
  }, []);

  // On iOS, the tab is fully suspended when BankID app opens.
  // Force a refetch of sign status when the tab becomes visible again.
  const getSignStatusRef = useRef(getSignStatus);
  getSignStatusRef.current = getSignStatus;

  useEffect(() => {
    const onVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        !hasHandledComplete.current
      ) {
        getSignStatusRef.current.refetch();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    if (createPOA.isSuccess) {
      advanceToNextStep();
    }
  }, [createPOA.isSuccess]);

  const handleRetryProcessSignup = async () => {
    setProcessError(false);
    setIsSignUpInProcess(true);

    try {
      await executeSignupFlow();
    } catch (error) {
      captureException(error, {
        tags: {
          operation: "retryProcessSignup",
          errorType:
            error instanceof Error
              ? error.name || "UnknownError"
              : "UnknownError",
        },
        level: "error",
      });
      setIsSignUpInProcess(false);
      setProcessError(true);
    }
  };

  const isLoading =
    getSignStatus.isLoading ||
    createSign.isPending ||
    createBooking.isPending ||
    isSignUpInProcess;

  // The app holds <POASign signing /> here instead, see its branch below.
  if (
    !app &&
    !complete &&
    getSignStatus.data?.body?.status === "started" &&
    (signProcessStatusInfo === "userSign" ||
      signProcessStatusInfo === "started")
  ) {
    return (
      <BankIdPendingMessage
        onCancel={() => {
          setComplete(true);
        }}
        processStatusInfo={signProcessStatusInfo}
      />
    );
  }

  if (returnedFromBankId) {
    return (
      <SigningComplete description="Du har signerat fullmakten. Återgå till din ursprungliga flik för att slutföra registreringen. Du kan stänga den här fliken." />
    );
  }

  if (isSignUpInProcess) {
    // The step renders into the layout's plain body, so centre it by hand.
    return app ? (
      <Center h="100%">
        <LoaderView />
      </Center>
    ) : (
      <LoaderComponent />
    );
  }

  if (processError) {
    return (
      <Flex justify="center" align="center">
        <Flex direction="column" maw={672} w={"100%"}>
          <RetryErrorCard
            title="Hoppsan! Något gick fel"
            description="Din signering gick igenom men vi kunde inte slutföra registreringen. Vänligen försök igen eller kontakta oss om problemet kvarstår."
            onRetry={handleRetryProcessSignup}
          >
            <ContactDetails />
          </RetryErrorCard>
        </Flex>
      </Flex>
    );
  }

  if (app && !getSignStatus.isError && !poaAlreadyExists && !poaBlockedUntil) {
    return <POASign signing={!autoStartToken} onSign={openSign} />;
  }

  return (
    <Flex justify="center" align="center">
      <Flex direction="column" maw={672} w={"100%"}>
        {poaBlockedUntil ? (
          <Paper p={isMobile ? "lg" : "xl"} radius={"24px"} mb={"2rem"}>
            <Flex direction={"column"}>
              <Title
                order={2}
                size="h5"
                mb={"1rem"}
                ff={"var(--wallet-font-family-sambla-sans)"}
              >
                Fullmakten kunde inte skapas
              </Title>
              <Text mb={"2rem"}>
                Vi hittade ingen pensionsinformation via din tidigare fullmakt,
                så det går inte att ge en ny fullmakt förrän{" "}
                {formatPoaDate(poaBlockedUntil)}. Om du har några frågor ring
                oss
              </Text>
            </Flex>
            <ContactDetails />
          </Paper>
        ) : poaAlreadyExists ? (
          <Paper p={isMobile ? "lg" : "xl"} radius={"24px"} mb={"2rem"}>
            <Flex direction={"column"}>
              <Title
                order={2}
                size="h5"
                mb={"1rem"}
                ff={"var(--wallet-font-family-sambla-sans)"}
              >
                Fullmakt har redan skapats
              </Title>
              <Text mb={"2rem"}>
                Vi har redan en fullmakt för dig. Om du har några frågor ring
                oss
              </Text>
            </Flex>
            <ContactDetails />
          </Paper>
        ) : getSignStatus.isError ? (
          <RetryErrorCard
            title="Hoppsan! Något gick fel"
            description="Det gick inte att hämta signeringsstatus. Det kan bero på ett tillfälligt fel. Vänligen försök igen."
            onRetry={restartSign}
          />
        ) : (
          <>
            <Paper p={30} radius={"24px"} mb={"2rem"}>
              <BankIdQrCode
                isLoading={isLoading}
                qrData={
                  getSignStatus.data?.body?.providerInfo?.seBankID?.qrData
                }
                autoStartToken={autoStartToken}
                error={getSignStatus.isError}
                instructionText="Starta BankID-appen på din mobila enhet och tryck på 'Skanna QR-kod'. Rikta kameran mot QR-koden och skriv under."
                buttonText="Signera med BankID"
                processStatusInfo={signProcessStatusInfo}
                nonce={nonceRef.current}
              />
            </Paper>
            <Paper p={"lg"} radius={"24px"} mb={"xl"}>
              <Flex justify={"center"}>
                <Image
                  src={FullmaktskollenImage}
                  alt="Fullmaktskollen"
                  h={38}
                  w="auto"
                  fit="contain"
                />
              </Flex>
            </Paper>
            <FooterButtons>
              <Button variant="default" onClick={goBack} data-testid="back">
                <FormattedMessage
                  id="bottomNav.back"
                  defaultMessage="Tillbaka"
                />
              </Button>
            </FooterButtons>
          </>
        )}
      </Flex>
    </Flex>
  );
}
